/* Sunpura mega menu: panels, native mobile accordions and one-shot ring motion. */
(() => {
  window.__sunpuraMegaMenuDestroy?.();
  const app = new AbortController();
  const signal = app.signal;
  const bindings = new Map();
  const running = new Map();
  const desktop = matchMedia('(min-width: 992px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const cardSelector = '.sp-mega-menu--panels .sp-mm-native-card[href]';
  const normalize = (text = '') => text.toString().trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ');
  const within = (scope, selector) => [ ...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector) ];

  function stop(card) {
    const state = running.get(card);
    if (!state) return;
    cancelAnimationFrame(state.frame);
    state.ring.style.removeProperty('--sp-mm-ring-angle');
    running.delete(card);
  }
  function stopWithin(scope) {
    for (const card of running.keys()) if (scope === document || scope.contains(card)) stop(card);
  }
  function run(card) {
    if (!desktop.matches || reduced.matches || document.hidden || running.has(card)) return;
    const ring = card.querySelector('.sp-mm-native-card__energy-head');
    if (!ring) return;
    const started = performance.now();
    const state = {ring, frame: 0};
    running.set(card, state);
    const tick = (now) => {
      if (!card.isConnected || !card.getClientRects().length) { stop(card); return; }
      const t = Math.min(1, (now - started) / 1120);
      const ease = t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      ring.style.setProperty('--sp-mm-ring-angle', `${225 + 360 * ease}deg`);
      if (t < 1) state.frame = requestAnimationFrame(tick);
      else stop(card); // 585deg and the static 225deg give the same final ring.
    };
    state.frame = requestAnimationFrame(tick);
  }

  function bindDesktop(menu) {
    if (bindings.has(menu)) return;
    const triggers = [...menu.querySelectorAll('.menu-sunpura_mega_panels .mega-menu__link--level-2')];
    const panels = [...menu.querySelectorAll('[data-sp-mm-panel]')];
    if (!triggers.length || !panels.length) return;
    const controller = new AbortController();
    const options = {signal: controller.signal};
    const byLabel = new Map();
    panels.forEach((panel, index) => {
      if (!panel.id) panel.id = `${menu.id}-sunpura-panel-${index}`;
      const label = normalize(panel.dataset.spMmTrigger);
      if (label && !byLabel.has(label)) byLabel.set(label, panel);
    });
    const targets = new Map(triggers.map(trigger => [trigger, byLabel.get(normalize(trigger.textContent))]));
    triggers.forEach(trigger => {
      trigger.removeAttribute('aria-selected');
      const panel = targets.get(trigger);
      if (panel) trigger.setAttribute('aria-controls', panel.id);
    });
    let active;
    const show = (trigger) => {
      if (active === trigger) return;
      const matched = targets.get(trigger);
      panels.forEach(panel => {
        panel.hidden = panel !== matched;
        if (panel.hidden) stopWithin(panel);
      });
      triggers.forEach(candidate => {
        const selected = Boolean(matched) && candidate === trigger;
        candidate.classList.toggle('sp-mm-trigger--active', selected);
        if (targets.get(candidate)) candidate.setAttribute('aria-expanded', String(selected));
      });
      active = trigger;
    };
    triggers.forEach((trigger, index) => {
      trigger.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') show(trigger); }, options);
      trigger.addEventListener('focus', () => show(trigger), options);
      trigger.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowDown') next = (index + 1) % triggers.length;
        if (event.key === 'ArrowUp') next = (index + triggers.length - 1) % triggers.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = triggers.length - 1;
        if (next !== undefined) { event.preventDefault(); triggers[next].focus(); }
        if (event.key === 'ArrowRight') {
          const first = targets.get(trigger)?.querySelector('a[href]');
          if (first) { event.preventDefault(); first.focus(); }
        }
      }, options);
    });
    show(triggers.find(trigger => targets.get(trigger) && trigger.getAttribute('aria-current') === 'page') || triggers.find(trigger => targets.get(trigger)) || triggers[0]);
    menu.addEventListener('toggle', event => {
      if (event.target !== menu) return;
      if (!menu.open) stopWithin(menu);
      menu.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(menu.open));
    }, options);
    menu.addEventListener('keydown', event => {
      if (event.key === 'Escape' && menu.open) {
        event.preventDefault(); event.stopPropagation(); menu.open = false;
        menu.querySelector(':scope > summary')?.focus();
      }
    }, options);
    bindings.set(menu, {controller, select(blockId) {
      const panel = panels.find(item => item.dataset.spMmBlock === blockId);
      const trigger = triggers.find(item => targets.get(item) === panel);
      if (!panel || !trigger) return;
      menu.open = true; show(trigger);
    }});
  }

  function bindMobile(group) {
    if (bindings.has(group)) return;
    const controller = new AbortController();
    const options = {signal: controller.signal};
    const summary = group.querySelector(':scope > summary');
    let handledEscape = false;
    const sync = () => summary?.setAttribute('aria-expanded', String(group.open));
    sync();
    // This accordion expands in place; bypass the theme's slide-in submenu handler.
    group.addEventListener('click', event => {
      if (event.target.closest('summary') === summary) event.stopPropagation();
    }, {...options, capture: true});
    group.addEventListener('toggle', event => {
      if (event.target !== group) return;
      sync();
      if (!group.open) return;
      group.closest('.menu-drawer__menu')?.querySelectorAll('.sp-mm-mobile-group[open]').forEach(other => {
        if (other !== group) other.open = false;
      });
    }, options);
    group.addEventListener('keydown', event => {
      if (event.key === 'Escape' && group.open) {
        handledEscape = true;
        event.preventDefault(); event.stopPropagation(); group.open = false; summary?.focus();
      }
    }, {...options, capture: true});
    // MenuDrawer handles Escape on keyup; do not let the same key close its parent.
    group.addEventListener('keyup', event => {
      if (event.key === 'Escape' && handledEscape) { event.stopPropagation(); handledEscape = false; }
    }, {...options, capture: true});
    bindings.set(group, {controller});
  }

  function init(scope = document) {
    within(scope, '.sp-mega-menu--panels').forEach(bindDesktop);
    within(scope, '.sp-mm-mobile-group').forEach(bindMobile);
  }
  function unload(scope) {
    stopWithin(scope);
    for (const [element, binding] of bindings) {
      if (scope === document || scope === element || scope.contains(element)) {
        binding.controller.abort(); bindings.delete(element);
      }
    }
  }
  for (const type of ['pointerover', 'focusin']) document.addEventListener(type, event => {
    const card = event.target.closest?.(cardSelector);
    if (!card || card.contains(event.relatedTarget) || event.pointerType === 'touch') return;
    run(card);
  }, {signal});
  for (const type of ['pointerout', 'focusout']) document.addEventListener(type, event => {
    const card = event.target.closest?.(cardSelector);
    if (!card || card.contains(event.relatedTarget)) return;
    if (!card.matches(':hover, :focus-visible')) stop(card);
  }, {signal});
  desktop.addEventListener('change', () => stopWithin(document), {signal});
  reduced.addEventListener('change', () => stopWithin(document), {signal});
  document.addEventListener('visibilitychange', () => { if (document.hidden) stopWithin(document); }, {signal});
  document.addEventListener('shopify:section:load', event => init(event.target), {signal});
  document.addEventListener('shopify:section:unload', event => unload(event.target), {signal});
  document.addEventListener('shopify:block:select', event => {
    for (const binding of bindings.values()) binding.select?.(event.detail?.blockId);
  }, {signal});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true, signal});
  else init();
  window.__sunpuraMegaMenuDestroy = () => { unload(document); app.abort(); };
})();
